import { apiUrl } from "../api/endpoints";
import React, { useState, useEffect, Fragment, useContext, useMemo, useCallback } from "react";
import CollapseIcon from "@mui/icons-material/IndeterminateCheckBoxOutlined";
import ExpandIcon from "@mui/icons-material/AddBoxOutlined";
import Spinner from "../Components/Spinner";
import { useAuth } from "../hooks/AuthProvider";
import { SearchIcon } from "../Icons";
import axios from "axios";
import Select from "react-select";
import "../Components/modal.css";
import "./edit.css";
import "./details.css";
import "./Permissions.css";

import { LanguageContext } from "../context/LanguageContext";
import permissionTranslations from "../languages/permission.json";

import Alert from "../Components/Alert";
import { getErrorMessage } from "../utils/errorUtils";
import PermissionsModal from "../Components/PermissionsModal";
import EditPermissionDataModal from "../Components/EditPermissionDataModal";
import { PermissionMainIcon, SettingsIcon } from "../svg/PermissionsIcon";
import { normalizeRoleListRow } from "../utils/listRowIds";

const SYSTEM_ROLE_NAMES = new Set(["super", "owner"]);
const isSystemRoleName = (name) => SYSTEM_ROLE_NAMES.has(String(name || "").trim().toLowerCase());

const getPermId = (p) => Number(p?.id);
const SUPER_ONLY_PERMISSION_IDS = new Set(
  String(process.env.REACT_APP_SUPER_ONLY_PERMISSION_IDS || "")
    .split(",")
    .map((value) => String(value || "").trim().toLowerCase())
    .filter(Boolean)
);

const getPermissionIdentifierTokens = (permission) => {
  const rawIds = permission?.ids;

  if (Array.isArray(rawIds)) {
    return rawIds
      .map((value) => String(value || "").trim().toLowerCase())
      .filter(Boolean);
  }

  return String(rawIds || "")
    .split(",")
    .map((value) => String(value || "").trim().toLowerCase())
    .filter(Boolean);
};

const isSuperOnlyPermission = (permission) =>
  getPermissionIdentifierTokens(permission).some((token) =>
    SUPER_ONLY_PERMISSION_IDS.has(token)
  );

const formatTranslation = (template, values = {}) =>
  Object.entries(values).reduce(
    (message, [key, value]) => message.replaceAll(`{${key}}`, value),
    template || ""
  );

const AssignedRolesDisplay = ({ assignedRoles, lang, emptyLabel, hideSystemRoles = false }) => {
  const isRtl = ["ar", "ur"].includes(lang);
  const names = (assignedRoles || []).filter(
    (role) => !hideSystemRoles || !isSystemRoleName(role?.name)
  );

  if (!names.length) {
    return <div className="permissions-roles-empty">{emptyLabel}</div>;
  }

  return (
    <div className={"permissions-roles-chips" + (isRtl ? " permissions-roles-chips-rtl" : "")}>
      {names.map((role) => (
        <span key={role.id || role.role_id || role.name} className="permissions-role-pill">
          {role.name}
        </span>
      ))}
    </div>
  );
};

const PermissionRow = ({
  permission,
  handleOpenEditModal,
  handleEditPermissionData,
  canEditPermissionLinks,
  canEditPermissionData,
  lang,
  hideSystemRoles,
  bulkMode,
  isSelected,
  onToggleSelect
}) => {
  const t = (permissionTranslations[lang] || permissionTranslations.en).permissionsPage;

  return (
    <div className={"permissions-row" + (bulkMode ? " permissions-row-bulk" : "")}>
      <div className="permissions-row-main">
        <div className="permissions-row-name-wrap">
          {bulkMode && (
            <input
              type="checkbox"
              className="permissions-bulk-checkbox"
              checked={!!isSelected}
              onChange={() => onToggleSelect(permission.id)}
            />
          )}
          <div className="permissions-row-name">{permission.name}</div>
        </div>

        <AssignedRolesDisplay
          assignedRoles={permission.Roles || []}
          lang={lang}
          emptyLabel={t.noRolesAssigned}
          hideSystemRoles={hideSystemRoles}
        />
      </div>

      <div className="permissions-row-actions">
        {canEditPermissionLinks && !bulkMode && (
          <button
            type="button"
            className="permissions-row-action-btn permissions-row-action-btn-primary"
            onClick={() => handleOpenEditModal(permission)}
          >
            {t.manageRoles}
          </button>
        )}

        {canEditPermissionData && !bulkMode && (
          <button
            type="button"
            className="permissions-row-action-btn permissions-row-action-btn-secondary"
            onClick={() => handleEditPermissionData(permission)}
          >
            {t.editData}
          </button>
        )}
      </div>
    </div>
  );
};

export const Permissions = () => {
  const { permissions: userPermissions, user, loading: authLoading } = useAuth();
  const normalizedUserRole = String(user?.role || "").trim().toLowerCase();
  const isUserRole = normalizedUserRole === "user";
  const isSuperUser = normalizedUserRole === "super";

  const canViewPermissions = !isUserRole && !!userPermissions["view_permissions"];
  const canViewRoles = !isUserRole && !!userPermissions["view_permissions"];
  const canEditPermissionLinks = !isUserRole && !!userPermissions["edit_permissions"];
  const canEditPermissionData = isSuperUser && !!userPermissions["edit_permissions"];

  const [loading, setLoading] = useState(true);
  const [subcategories, setSubcategories] = useState([]);
  const [expanded, setExpanded] = useState({});
  const [permissions, setPermissions] = useState([]);
  const [initialPermissions, setInitialPermissions] = useState([]);
  const [query, setQuery] = useState("");

  const { lang } = useContext(LanguageContext);
  const translations = permissionTranslations[lang] || permissionTranslations.en;
  const t = translations.permissionsPage;
  const isRtl = ["ar", "ur"].includes(lang);

  const [alertState, setAlertState] = useState({
    open: false,
    message: "",
    type: "info",
    position: isRtl ? "left" : "right"
  });

  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [permissionToEdit, setPermissionToEdit] = useState(null);

  const [isEditDataModalOpen, setIsEditDataModalOpen] = useState(false);
  const [permissionDataToEdit, setPermissionDataToEdit] = useState(null);

  const [bulkMode, setBulkMode] = useState(false);
  const [roles, setRoles] = useState([]);
  const [rolesLoading, setRolesLoading] = useState(false);
  const [subscribers, setSubscribers] = useState([]);
  const [targetSubid, setTargetSubid] = useState("");

  const [bulkSelectedPermissionIds, setBulkSelectedPermissionIds] = useState(new Set());
  const [bulkSelectedRoleIds, setBulkSelectedRoleIds] = useState(new Set());

  const [stagedRoleChanges, setStagedRoleChanges] = useState({});
  const [isApplying, setIsApplying] = useState(false);

  const subscriberOptions = useMemo(
    () =>
      subscribers.map((subscriber) => ({
        value: String(subscriber.id),
        label: subscriber.name || subscriber.email || `Subscriber ${subscriber.id}`
      })),
    [subscribers]
  );

  const selectedSubscriberOption = useMemo(
    () => subscriberOptions.find((option) => option.value === String(targetSubid)) || null,
    [subscriberOptions, targetSubid]
  );

  const showAlert = useCallback(
    (type, message) => {
      setAlertState({
        open: true,
        type,
        message,
        position: isRtl ? "left" : "right"
      });
    },
    [isRtl]
  );

  const systemRoleIdsFromRolesList = useMemo(() => {
    return (Array.isArray(roles) ? roles : [])
      .filter((r) => isSystemRoleName(r?.name))
      .map((r) => Number(r?.role_id || r?.id))
      .filter(Number.isFinite);
  }, [roles]);

  const displayableRoles = useMemo(
    () => (isSuperUser ? roles : roles.filter((role) => !isSystemRoleName(role?.name))),
    [isSuperUser, roles]
  );

  const visiblePermissions = useMemo(
    () =>
      isSuperUser
        ? permissions
        : permissions.filter((permission) => !isSuperOnlyPermission(permission)),
    [isSuperUser, permissions]
  );

  const rolesById = useMemo(() => {
    const map = new Map();
    (Array.isArray(roles) ? roles : []).forEach((role) => {
      const rid = Number(role?.role_id || role?.id);
      if (Number.isFinite(rid)) {
        map.set(rid, role);
      }
    });
    return map;
  }, [roles]);

  const enforceSystemRoleIds = useCallback(
    (roleIds) => {
      const next = new Set(
        (Array.isArray(roleIds) ? roleIds : [])
          .map(Number)
          .filter(Number.isFinite)
      );
      if (!isSuperUser) {
        systemRoleIdsFromRolesList.forEach((rid) => next.add(rid));
      }
      return Array.from(next);
    },
    [isSuperUser, systemRoleIdsFromRolesList]
  );

  const fetchAllData = useCallback(async () => {
    setLoading(true);
    try {
      const permissionResponse = await axios.get(apiUrl(`permission/list_with_roles`), {
        params: isSuperUser && targetSubid ? { target_subid: targetSubid } : undefined
      });

      if (permissionResponse.data?.status === "OK" && Array.isArray(permissionResponse.data.data)) {
        setPermissions(permissionResponse.data.data);
        setInitialPermissions(permissionResponse.data.data);
      } else {
        showAlert("error", t.invalidPermissionData);
      }
    } catch (err) {
      showAlert("error", formatTranslation(t.loadPageDataFailed, { error: getErrorMessage(err) }));
      setPermissions([]);
      setInitialPermissions([]);
    } finally {
      setLoading(false);
    }
  }, [isSuperUser, showAlert, t.invalidPermissionData, t.loadPageDataFailed, targetSubid]);

  const fetchRoles = useCallback(async () => {
    if (!canViewRoles) return;

    setRolesLoading(true);

    try {
      const res = await axios.get(apiUrl(`roles`), {
        params: isSuperUser && targetSubid ? { target_subid: targetSubid } : undefined
      });

      if (res.data?.status === "OK" && Array.isArray(res.data.data)) {
        const mapped = res.data.data.map(normalizeRoleListRow);
        setRoles(mapped);
      } else {
        console.error("Received unexpected data format from /roles:", res.data);
        setRoles([]);
        showAlert("error", t.invalidRolesFormat);
      }
    } catch (err) {
      const errorMessage = getErrorMessage(err);
      if (err.response && err.response.status === 403) {
        showAlert("error", t.viewRolesDenied);
      } else {
        showAlert("error", errorMessage);
      }
      setRoles([]);
    } finally {
      setRolesLoading(false);
    }
  }, [canViewRoles, isSuperUser, showAlert, t.invalidRolesFormat, t.viewRolesDenied, targetSubid]);

  const fetchSubscribers = useCallback(async () => {
    if (!isSuperUser) return;

    try {
      const res = await axios.get(apiUrl(`subscriber`));
      const list = Array.isArray(res.data?.data) ? res.data.data : [];
      setSubscribers(list);
      setTargetSubid((current) => current || (list[0]?.id ? String(list[0].id) : ""));
    } catch (err) {
      showAlert("error", getErrorMessage(err));
      setSubscribers([]);
    }
  }, [isSuperUser, showAlert]);

  useEffect(() => {
    if (!authLoading) {
      if (canViewPermissions) {
        if (isSuperUser && !targetSubid) {
          fetchSubscribers();
        } else {
          fetchAllData();
          fetchRoles();
        }
      } else {
        setLoading(false);
      }
    }
  }, [canViewPermissions, authLoading, fetchAllData, fetchRoles, fetchSubscribers, isSuperUser, targetSubid]);

  useEffect(() => {
    if (!canViewPermissions || authLoading) return;
    if (isSuperUser && !targetSubid) return;
    fetchAllData();
    fetchRoles();
  }, [authLoading, canViewPermissions, fetchAllData, fetchRoles, isSuperUser, targetSubid]);

  useEffect(() => {
    if (isSuperUser) return;
    if (!bulkMode) return;
    if (!systemRoleIdsFromRolesList.length) return;

    setBulkSelectedRoleIds((prev) => {
      const next = new Set(prev);
      systemRoleIdsFromRolesList.forEach((rid) => next.add(rid));
      return next;
    });
  }, [bulkMode, isSuperUser, systemRoleIdsFromRolesList]);

  useEffect(() => {
    let titles = [];
    let subs = [];
    const initialExpanded = {};

    initialPermissions.forEach((i) => {
      titles.push(i.category);
      subs.push({ category: i.category, subcategory: i.subcategory });
    });

    titles = [...new Set(titles)];
    titles.forEach((title) => (initialExpanded[title] = false));

    subs = subs.filter(
      (value, index, self) =>
        index ===
        self.findIndex(
          (i) =>
            value.subcategory !== "-" && i.category === value.category && i.subcategory === value.subcategory
        )
    );

    setSubcategories(subs);

    setExpanded((prev) => {
      if (Object.keys(prev || {}).length) return prev;
      return initialExpanded;
    });
  }, [initialPermissions]);

  const toggleExpanded = (key) => {
    setExpanded((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const setAll = (value) => {
    const newExpanded = {};
    Object.keys(expanded).forEach((k) => (newExpanded[k] = value));
    setExpanded(newExpanded);
  };

  const handleOpenEditModal = (permission) => {
    if (!canEditPermissionLinks) {
      showAlert("error", t.editLinksDenied);
      return;
    }
    if (!isSuperUser && isSuperOnlyPermission(permission)) {
      showAlert("error", t.noPermission);
      return;
    }
    setPermissionToEdit(permission);
    setIsEditModalOpen(true);
  };

  const handleCloseEditModal = () => {
    setPermissionToEdit(null);
    setIsEditModalOpen(false);
  };

  const handleEditPermissionData = (permission) => {
    if (!canEditPermissionData) {
      showAlert("error", t.editDataDenied);
      return;
    }
    if (!isSuperUser && isSuperOnlyPermission(permission)) {
      showAlert("error", t.noPermission);
      return;
    }
    setPermissionDataToEdit(permission);
    setIsEditDataModalOpen(true);
  };

  const handleCloseEditDataModal = () => {
    setPermissionDataToEdit(null);
    setIsEditDataModalOpen(false);
  };

  const togglePermissionSelect = (permissionId) => {
    const pid = Number(permissionId);
    setBulkSelectedPermissionIds((prev) => {
      const next = new Set(prev);
      if (next.has(pid)) next.delete(pid);
      else next.add(pid);
      return next;
    });
  };

  const toggleRoleSelect = (roleId) => {
    const rid = Number(roleId);

    if (!isSuperUser && systemRoleIdsFromRolesList.includes(rid)) {
      showAlert("info", t.systemRolesLocked);
      setBulkSelectedRoleIds((prev) => {
        const next = new Set(prev);
        next.add(rid);
        return next;
      });
      return;
    }

    setBulkSelectedRoleIds((prev) => {
      const next = new Set(prev);
      if (next.has(rid)) next.delete(rid);
      else next.add(rid);
      if (!isSuperUser) {
        systemRoleIdsFromRolesList.forEach((id) => next.add(id));
      }
      return next;
    });
  };

  const getEffectiveRoleIdsForPermission = (permission) => {
    const pid = getPermId(permission);
    if (Array.isArray(stagedRoleChanges[pid])) return stagedRoleChanges[pid];

    const current = Array.isArray(permission.Roles) ? permission.Roles : [];
    return current.map((r) => Number(r.role_id || r.id)).filter((n) => Number.isFinite(n));
  };

  const getEffectiveRolesForPermission = (permission) => {
    const currentRoles = Array.isArray(permission?.Roles) ? permission.Roles : [];
    const currentRolesById = new Map(
      currentRoles
        .map((role) => [Number(role?.role_id || role?.id), role])
        .filter(([rid]) => Number.isFinite(rid))
    );

    return getEffectiveRoleIdsForPermission(permission)
      .map((rid) => rolesById.get(rid) || currentRolesById.get(rid))
      .filter(Boolean);
  };

  const bulkGrantOrRevoke = (action) => {
    if (!bulkSelectedPermissionIds.size) {
      showAlert("error", t.selectPermissionsFirst);
      return;
    }
    if (!bulkSelectedRoleIds.size) {
      showAlert("error", t.selectRolesFirst);
      return;
    }

    const roleIdsEffective = Array.from(bulkSelectedRoleIds)
      .map(Number)
      .filter(Number.isFinite)
      .filter((rid) => isSuperUser || !systemRoleIdsFromRolesList.includes(rid));

    if (!roleIdsEffective.length) {
      showAlert("error", t.selectRolesFirst);
      return;
    }

    setStagedRoleChanges((prev) => {
      const next = { ...prev };

      permissions.forEach((perm) => {
        const pid = getPermId(perm);
        if (!bulkSelectedPermissionIds.has(pid)) return;

        const current = new Set(getEffectiveRoleIdsForPermission(perm));

        if (action === "grant") {
          roleIdsEffective.forEach((rid) => current.add(rid));
        } else {
          roleIdsEffective.forEach((rid) => current.delete(rid));
        }

        next[pid] = enforceSystemRoleIds(Array.from(current));
      });

      return next;
    });

    showAlert(
      "success",
      action === "grant" ? t.bulkGranted : t.bulkRevoked
    );
  };

  const pendingCount = useMemo(() => Object.keys(stagedRoleChanges).length, [stagedRoleChanges]);

  const handleApplyAll = async () => {
    if (!pendingCount) return;

    setIsApplying(true);
    try {
      const changes = Object.entries(stagedRoleChanges).map(([permission_id, role_ids]) => {
        const pid = Number(permission_id);
        const roleIds = Array.isArray(role_ids) ? role_ids.map(Number) : [];
        const enforced = enforceSystemRoleIds(roleIds);

        return {
          permission_id: pid,
          role_ids: enforced
        };
      });

      await axios.post(apiUrl(`permission-roles/bulk-set`), {
        changes,
        ...(isSuperUser && targetSubid ? { target_subid: Number(targetSubid) } : {})
      });

      showAlert("success", t.savedSuccessfully);

      setStagedRoleChanges({});
      setBulkSelectedPermissionIds(new Set());
      setBulkSelectedRoleIds(new Set(isSuperUser ? [] : systemRoleIdsFromRolesList));
      await fetchAllData();
    } catch (err) {
      showAlert("error", formatTranslation(t.applyFailed, { error: getErrorMessage(err) }));
    } finally {
      setIsApplying(false);
    }
  };

  const handleDiscardAll = async () => {
    setStagedRoleChanges({});
    showAlert("info", t.changesDiscarded);
    await fetchAllData();
  };

  const toggleBulkMode = () => {
    setBulkMode((b) => {
      const next = !b;
      if (!next) {
        setBulkSelectedPermissionIds(new Set());
        setBulkSelectedRoleIds(new Set());
      } else {
        setBulkSelectedRoleIds((prev) => {
          const s = new Set(prev);
          if (!isSuperUser) {
            systemRoleIdsFromRolesList.forEach((rid) => s.add(rid));
          }
          return s;
        });
      }
      return next;
    });
  };

  const handleTargetSubscriberChange = (option) => {
    setTargetSubid(option?.value || "");
    setStagedRoleChanges({});
    setBulkSelectedPermissionIds(new Set());
    setBulkSelectedRoleIds(new Set());
    setExpanded({});
  };

  if (authLoading) return <Spinner />;

  if (!canViewPermissions) {
    return <div className="permissions-no-permission">{t.noPermission}</div>;
  }

  if (loading) return <Spinner />;

  const filteredPermissions =
    query.length === 0
      ? visiblePermissions
      : visiblePermissions.filter((p) => p.name.toLowerCase().includes(query.toLowerCase()));

  const renderPermissionList = (permissionsList) =>
    permissionsList.map((p) => (
      <PermissionRow
        key={p.id}
        permission={{
          ...p,
          Roles: getEffectiveRolesForPermission(p)
        }}
        handleOpenEditModal={handleOpenEditModal}
        handleEditPermissionData={handleEditPermissionData}
        canEditPermissionLinks={canEditPermissionLinks}
        canEditPermissionData={canEditPermissionData}
        lang={lang}
        hideSystemRoles={!isSuperUser}
        bulkMode={bulkMode}
        isSelected={bulkSelectedPermissionIds.has(Number(p.id))}
        onToggleSelect={togglePermissionSelect}
      />
    ));

  return (
    <Fragment>
      {isEditModalOpen && permissionToEdit && (
        <PermissionsModal
          permission={permissionToEdit}
          onClose={handleCloseEditModal}
          showAlert={showAlert}
          hideSystemRoles={!isSuperUser}
          lockSystemRoles={!isSuperUser}
          stagedRoleIds={stagedRoleChanges[Number(permissionToEdit.id)] ?? null}
          onStage={(permissionId, roleIds) => {
            const pid = Number(permissionId);

            const enforcedRoleIds = enforceSystemRoleIds(roleIds);

            setStagedRoleChanges((prev) => ({
              ...prev,
              [pid]: enforcedRoleIds
            }));

            showAlert("success", t.stagedOne);
          }}
          targetSubid={isSuperUser ? targetSubid : null}
        />
      )}

      {isEditDataModalOpen && permissionDataToEdit && (
        <EditPermissionDataModal
          permission={permissionDataToEdit}
          onClose={handleCloseEditDataModal}
          onUpdate={fetchAllData}
          showAlert={showAlert}
        />
      )}

      <div
        id="permissions-page-root"
        className={isRtl ? "configuration permissions-page permissions-rtl" : "configuration permissions-page"}
      >
        <Alert
          open={alertState.open}
          close={() => setAlertState((prev) => ({ ...prev, open: false }))}
          message={alertState.message}
          type={alertState.type}
          position={alertState.position}
        />

        <header className="permissions-header">
          <div className="permissions-header-main">
            <div className="permissions-header-icon-wrapper">
              <PermissionMainIcon />
            </div>
            <div className="permissions-header-text">
              <h2 className="permissions-header-title">{t.title}</h2>
              <p className="permissions-header-subtitle">{t.subtitle}</p>
            </div>
          </div>

          <div className="permissions-header-controls">
            {isSuperUser && (
              <Select
                className="permissions-subscriber-select"
                classNamePrefix="permissions-subscriber-select"
                value={selectedSubscriberOption}
                options={subscriberOptions}
                onChange={handleTargetSubscriberChange}
                isDisabled={loading || subscribers.length === 0}
                isSearchable
                placeholder={t.targetSubscriber || "Subscriber"}
                menuPortalTarget={document.body}
                menuPosition="fixed"
              />
            )}

            {canEditPermissionLinks && (
              <button
                type="button"
                className={"permissions-bulk-toggle" + (bulkMode ? " permissions-bulk-toggle-on" : "")}
                onClick={toggleBulkMode}
              >
                {bulkMode ? t.bulkModeOn : t.bulkModeOff}
              </button>
            )}
          </div>
        </header>

        {canEditPermissionLinks && pendingCount > 0 && (
          <div className="permissions-applybar">
            <div className="permissions-applybar-left">
              <span className="permissions-applybar-title">
                {t.pendingChanges}: {pendingCount}
              </span>
              <span className="permissions-applybar-hint">{t.applyHint}</span>
            </div>

            <div className="permissions-applybar-actions">
              <button
                type="button"
                className="permissions-row-action-btn permissions-row-action-btn-secondary"
                onClick={handleDiscardAll}
                disabled={isApplying}
              >
                {t.discard}
              </button>

              <button
                type="button"
                className="permissions-row-action-btn permissions-row-action-btn-primary"
                onClick={handleApplyAll}
                disabled={isApplying}
              >
                {isApplying ? t.saving : t.applyAll}
              </button>
            </div>
          </div>
        )}

        <div className="permissions-layout">
          <section className="permissions-panel">
            <div className="permissions-panel-header">
              <div className="permissions-panel-title-row">
                <span className="permissions-panel-title">{t.permissionName}</span>

                <div className="permissions-panel-toggle-links">
                  <button type="button" className="permissions-toggle-link" onClick={() => setAll(false)}>
                    {t.collapseAll}
                  </button>
                  <span className="permissions-toggle-separator">|</span>
                  <button type="button" className="permissions-toggle-link" onClick={() => setAll(true)}>
                    {t.expandAll}
                  </button>
                </div>
              </div>

              <div className="permissions-search">
                <span className="permissions-search-icon">
                  <SearchIcon />
                </span>
                <input
                  type="text"
                  className="permissions-search-input"
                  placeholder={t.placeholderSearch}
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                />
              </div>
            </div>

            <div className="permissions-panel-body">
              {query.length === 0 ? (
                Object.keys(expanded).map((categoryKey) => {
                  const categoryPermissions = filteredPermissions.filter((p) => p.category === categoryKey);
                  if (!categoryPermissions.length) return null;

                  return (
                    <div className="permissions-category" key={categoryKey}>
                      <button
                        type="button"
                        className={"permissions-category-header" + (expanded[categoryKey] ? " permissions-category-header-open" : "")}
                        onClick={() => toggleExpanded(categoryKey)}
                      >
                        <span className="permissions-category-chevron">
                          {expanded[categoryKey] ? <CollapseIcon fontSize="small" /> : <ExpandIcon fontSize="small" />}
                        </span>
                        <span className="permissions-category-name">{categoryKey}</span>
                        <span className="permissions-category-count">{categoryPermissions.length}</span>
                      </button>

                      {expanded[categoryKey] && (
                        <div className="permissions-category-body">
                          {renderPermissionList(categoryPermissions.filter((p) => p.subcategory === "-"))}

                          {subcategories.map(
                            (sub) =>
                              sub.category === categoryKey && (
                                <Fragment key={sub.subcategory}>
                                  <div className="permissions-subcategory-header">{sub.subcategory}</div>
                                  {renderPermissionList(categoryPermissions.filter((p) => p.subcategory === sub.subcategory))}
                                </Fragment>
                              )
                          )}
                        </div>
                      )}
                    </div>
                  );
                })
              ) : (
                <div className="permissions-flat-results">{renderPermissionList(filteredPermissions)}</div>
              )}
            </div>
          </section>

          <aside className="permissions-sidebar">
            {bulkMode && canEditPermissionLinks ? (
              <div className="permissions-bulk-card">
                <div className="permissions-quick-header">
                  <span className="permissions-quick-icon">
                    <SettingsIcon />
                  </span>
                  <span className="permissions-quick-title">{t.bulkTitle}</span>
                </div>

                <div className="permissions-bulk-section">
                  <div className="permissions-bulk-section-title">
                    {t.selectedPermissions}: <b>{bulkSelectedPermissionIds.size}</b>
                  </div>

                  <div className="permissions-bulk-section-title" style={{ marginTop: 10 }}>
                    {t.selectRoles}
                  </div>

                  <div className="permissions-bulk-roles">
                    {rolesLoading ? (
                      <div className="permissions-bulk-empty">{t.loadingRoles}</div>
                    ) : displayableRoles.length === 0 ? (
                      <div className="permissions-bulk-empty">{t.noRolesLoaded}</div>
                    ) : (
                      displayableRoles.map((r) => {
                        const rid = Number(r.role_id || r.id);
                        const systemRole = isSystemRoleName(r.name);

                        const locked = !isSuperUser && systemRole;
                        const checked = locked ? true : bulkSelectedRoleIds.has(rid);
                        const disabled = locked;

                        return (
                          <label key={rid} className="permissions-bulk-role">
                            <span className="permissions-bulk-role-name">
                              {r.name}
                              {systemRole ? (
                                <span style={{ marginInlineStart: 8, opacity: 0.85 }}>
                                  ({t.systemRoleLabel})
                                </span>
                              ) : null}
                            </span>

                            <input
                              type="checkbox"
                              checked={checked}
                              disabled={disabled}
                              onChange={() => toggleRoleSelect(rid)}
                            />
                          </label>
                        );
                      })
                    )}
                  </div>

                  {!isSuperUser && roles.length !== displayableRoles.length ? (
                    <div className="permissions-bulk-hint permissions-system-note">{t.restrictedRolesNotice}</div>
                  ) : null}

                  <div className="permissions-bulk-actions">
                    <button
                      type="button"
                      className="permissions-bulk-btn permissions-bulk-btn-grant"
                      onClick={() => bulkGrantOrRevoke("grant")}
                    >
                      {t.grant}
                    </button>

                    <button
                      type="button"
                      className="permissions-bulk-btn permissions-bulk-btn-revoke"
                      onClick={() => bulkGrantOrRevoke("revoke")}
                    >
                      {t.revoke}
                    </button>
                  </div>

                  <div className="permissions-bulk-hint">{t.bulkHint}</div>
                </div>
              </div>
            ) : (
              <>

                <div className="permissions-about-card">
                  <div className="permissions-about-icon-wrapper">
                    <PermissionMainIcon />
                  </div>
                  <div className="permissions-about-content">
                    <h3 className="permissions-about-title">{t.aboutTitle}</h3>
                    <p className="permissions-about-text">{t.aboutDescription}</p>
                  </div>
                </div>
              </>
            )}
          </aside>
        </div>
      </div>
    </Fragment>
  );
};

export default Permissions;
